import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
	type CanonicalValues,
	ImportChangeOutcome,
	type ImportExecutionChangeDTO,
	type ImportExecutionDetailDTO,
	ImportPlanAction,
	Permission,
} from '@today/types'
import type {
	ImportChangeRowView,
	ImportExecutionDetailViewProps,
	ImportExecutionSummaryView,
	ImportValidationErrorRowView,
} from '@today/ui-domain'
import type { TFunction } from 'i18next'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useViewerPermissions } from '@/auth/useViewerPermissions'
import { IMPORTS_NAMESPACE } from '@/i18n'
import { importMonitoringService } from '@/services/import-monitoring.service'
import { DASH, enumOptions, listParams, openDownload, toRowView } from './imports-ui'

const fieldValue = (values: CanonicalValues | null, field: string): string => {
	const value = values?.[field]
	return value === null || value === undefined ? DASH : String(value)
}

/** `field: before → after` for each changed field (or every field of a create / deactivate). */
const diffOf = (change: ImportExecutionChangeDTO): string => {
	const fields = change.changedFields.length ? change.changedFields : Object.keys(change.after ?? change.before ?? {})
	return fields.map((f) => `${f}: ${fieldValue(change.before, f)} → ${fieldValue(change.after, f)}`).join('; ') || DASH
}

const toChangeRow = (change: ImportExecutionChangeDTO, t: TFunction): ImportChangeRowView => ({
	id: String(change.seq),
	seq: String(change.seq),
	sourceLine: change.sourceLine === null ? DASH : String(change.sourceLine),
	actionLabel: t(`action.${change.action}`),
	outcomeLabel: t(`outcome.${change.outcome}`),
	key: change.key?.map(String).join(', ') || DASH,
	diff: diffOf(change),
	errorCode: change.errorCode ?? DASH,
})

const toSummary = (d: ImportExecutionDetailDTO, t: TFunction, language: string): ImportExecutionSummaryView => ({
	...toRowView(d, t, language),
	failureCode: d.failureCode ?? DASH,
	sha256: d.sha256 ?? DASH,
	validationOutcomeLabel: d.validation ? t(`validationOutcome.${d.validation.outcome}`) : DASH,
	failedStageLabel: d.validation?.failedStage ? t(`stage.${d.validation.failedStage}`) : DASH,
	totalErrors: d.validation ? String(d.validation.totalErrors) : DASH,
})

/** The summary's first validation errors — never a value. */
const toErrorRows = (d: ImportExecutionDetailDTO | undefined, t: TFunction): ImportValidationErrorRowView[] =>
	(d?.validation?.errors ?? []).map((e, index) => ({
		id: String(index),
		recordNumber: e.recordNumber === null ? DASH : String(e.recordNumber),
		stageLabel: t(`stage.${e.stage}`),
		code: e.code,
		field: e.targetField ?? e.sourceField ?? DASH,
	}))

/** Requests a link on click and opens it; a failure shows the message and opens nothing (BR-11). */
function useDownloads(id: string) {
	const [failed, setFailed] = useState(false)
	const download = (request: (id: string) => Promise<{ url: string }>) => () => {
		setFailed(false)
		request(id).then(
			(link) => openDownload(link.url),
			() => setFailed(true),
		)
	}
	return {
		failed,
		errors: download(importMonitoringService.errorsReportLink),
		original: download(importMonitoringService.originalFileLink),
	}
}

/**
 * One execution's drill-down (TO-399 REQ-010): the detail, its paged change log
 * (action / outcome filters reset to page 1) and the downloads — the errors CSV
 * when the report exists, the original file only with
 * `import_execution.original_file.read` and when it is available.
 */
export function useImportExecutionDetail(id: string, onBack: () => void): ImportExecutionDetailViewProps {
	const { t, i18n } = useTranslation(IMPORTS_NAMESPACE)
	const { permissions } = useViewerPermissions()
	const [action, setAction] = useState('')
	const [outcome, setOutcome] = useState('')
	const [page, setPage] = useState(1)
	const params = listParams(page, { action, outcome })
	const detail = useQuery({
		queryKey: ['imports', 'execution', id],
		queryFn: () => importMonitoringService.get(id),
	})
	const changes = useQuery({
		queryKey: ['imports', 'changes', id, params],
		queryFn: () => importMonitoringService.changes(id, params),
		placeholderData: keepPreviousData,
	})
	const downloads = useDownloads(id)
	const d = detail.data
	const mayDownloadOriginal = permissions.includes(Permission.IMPORT_EXECUTION_ORIGINAL_FILE_READ)
	return {
		t,
		summary: d ? toSummary(d, t, i18n.language) : null,
		error: detail.isError,
		validationErrors: toErrorRows(d, t),
		changes: (changes.data?.data ?? []).map((c) => toChangeRow(c, t)),
		changesPage: page,
		changesPageCount: changes.data?.totalPages ?? 0,
		actionFilter: action,
		actionOptions: enumOptions(t, 'action', Object.values(ImportPlanAction)),
		outcomeFilter: outcome,
		outcomeOptions: enumOptions(t, 'outcome', Object.values(ImportChangeOutcome)),
		downloadFailed: downloads.failed,
		onActionChange: (next) => {
			setAction(next)
			setPage(1)
		},
		onOutcomeChange: (next) => {
			setOutcome(next)
			setPage(1)
		},
		onChangesPageChange: setPage,
		onDownloadErrors: d?.errorsReportAvailable ? downloads.errors : undefined,
		onDownloadOriginal: d?.originalFileAvailable && mayDownloadOriginal ? downloads.original : undefined,
		onBack,
		onRetry: () => void detail.refetch(),
	}
}
